import { Capacitor } from "@capacitor/core";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "../types/database";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabasePublishableKey) {
  console.error("❌ Missing Supabase environment variables");
  throw new Error("Missing Supabase environment variables");
}

export const supabase = createClient<Database>(
  supabaseUrl,
  supabasePublishableKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // The iOS app receives the OAuth result through a custom URL scheme any
      // app could open, so it uses PKCE (the code is useless without the
      // verifier kept in this app). The web app stays on the implicit flow:
      // with PKCE, a leftover verifier plus /invite?code=... would be treated
      // as an OAuth callback and sign the user out.
      flowType: Capacitor.isNativePlatform() ? "pkce" : "implicit",
      debug: false,
    },
  }
);

/**
 * Invoke a Supabase Edge Function with proper auth headers.
 * Handles both the apikey header (required by the API gateway in production)
 * and the Authorization header (user's session token).
 */
export async function invokeEdgeFunction(
  functionPath: string,
  options: {
    method?: string;
    headers?: Record<string, string>;
    body?: RequestInit['body'];
  } = {}
): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');

  return fetch(`${supabaseUrl}/functions/v1/${functionPath}`, {
    method: options.method ?? 'POST',
    headers: {
      apikey: supabasePublishableKey,
      Authorization: `Bearer ${session.access_token}`,
      ...options.headers,
    },
    body: options.body,
  });
}
